import React, { useState } from "react"
import styles from "./FeedCardComment.module.scss"
import useUserImage from "../../../hooks/useUserImage"
import { ReactComponent as NoImage } from "../../../assets/svg/noImageIcon.svg"
import TextareaAutosize from "react-textarea-autosize"
import { connect } from "react-redux"
import {
  deletePostComment,
  editPostComment,
  createPostComment,
  likePostComment,
  responsePostComment,
} from "../../../store/actions"
import { Send, X, Heart } from "lucide-react"
import ResponseComments from "./ResponseComments.tsx"
import { motion } from "framer-motion"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import type { FeedComment, FeedPost } from "../../../types/feed.ts"

const DEFAULT_PROFILE_IMAGE =
  "https://edify-files-dev.s3.amazonaws.com/profile-pictures/ebba9aa8-4346-4662-b3e5-8699015659ba-image-profile-default.png"

const responseVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
}

interface FeedCardCommentProps {
  comments: FeedComment[]
  commentsCount: number
  showComments: number | false
  createPostComment: (postId: number, content: string) => Promise<unknown> | void
  likePostComment: (commentId: number, postId: number) => Promise<unknown> | void
  userData: FeedPost["user"] & { id?: number | string }
  id: number
  responsePostComment: (postId: number, commentId: number, content: string) => Promise<unknown> | void
  deletePostComment: (commentId: number, postId: number) => Promise<unknown> | void
  editPostComment: (commentId: number, content: string, postId: number) => Promise<unknown> | void
  calcularDiferenciaTiempo: (fechaCreacion: string) => string
  setShowComments: (v: number | false) => void
  isHovered: boolean
  commentsLoaded?: boolean
  loadComments?: () => void
}

const FeedCardComment: React.FC<FeedCardCommentProps> = ({
  comments,
  commentsCount,
  showComments,
  createPostComment,
  likePostComment,
  userData,
  id,
  responsePostComment,
  deletePostComment,
  editPostComment,
  calcularDiferenciaTiempo,
  setShowComments,
  isHovered,
}) => {
  const [commentEditing, setCommentEditing] = useState<number | false>(false)
  const [editComment, setEditComment] = useState("")
  const [menu, setMenu] = useState<number | null>(null)
  const [commentContent, setCommentContent] = useState("")
  const [commentResponse, setCommentResponse] = useState("")
  const [showResponseInput, setShowResponseInput] = useState<number | false>(false)
  const [submittingComment, setSubmittingComment] = useState(false)
  const [loadingAction, setLoadingAction] = useState<string | null>(null)
  const { guard } = useActionGuard()

  const { imageError, initials, profileImage } = useUserImage(userData)

  if (!showComments) return null

  const handleToggleMenu = (commentId: number) => {
    setMenu(menu === commentId ? null : commentId)
  }

  const handleMenuAction = (action: string, commentId: number) => {
    if (action === "Editar") {
      setCommentEditing(commentId)
    } else if (action === "Eliminar") {
      handleDeleteComment(commentId)
    }
  }

  const handlePostComment = (postId: number, content: string) => {
    setSubmittingComment(true)
    const p = createPostComment(postId, content)
    if (p instanceof Promise) p.finally(() => setSubmittingComment(false))
    else setSubmittingComment(false)
    setCommentContent("")
    setShowComments(id)
  }

  const handleDeleteComment = (commentId: number) => {
    setLoadingAction(`delete-${commentId}`)
    const p = deletePostComment(commentId, id)
    if (p instanceof Promise) p.finally(() => setLoadingAction(null))
    else setLoadingAction(null)
  }

  const handleEditComment = (commentId: number, content: string) => {
    setLoadingAction(`edit-${commentId}`)
    const p = editPostComment(commentId, content, id)
    if (p instanceof Promise) p.finally(() => setLoadingAction(null))
    else setLoadingAction(null)
    setCommentEditing(false)
  }

  const handleLikeComment = guard("like_post_comment", (commentId: number) => {
    setLoadingAction(`like-${commentId}`)
    const p = likePostComment(commentId, id)
    if (p instanceof Promise) p.finally(() => setLoadingAction(null))
    else setLoadingAction(null)
  })

  const handleShowResponseInput = (commentId: number) => {
    if (showResponseInput === commentId) {
      setShowResponseInput(false)
      return
    }
    setShowResponseInput(commentId)
  }

  const handleResponseComment = guard(
    "response_post_comment",
    (commentId: number, content: string) => {
      if (content.length >= 2) {
        setLoadingAction(`response-${commentId}`)
        const p = responsePostComment(id, commentId, content)
        if (p instanceof Promise) p.finally(() => setLoadingAction(null))
        else setLoadingAction(null)
        setCommentResponse("")
        setShowResponseInput(false)
      }
    }
  )

  return (
    <>
      <div className={styles.itemCommentHeader}>
        <div className={styles.itemCommentHeader_imgContainer}>
          {!imageError && profileImage ? (
            <img
              className={styles.itemCommentHeader_imgContainer_img}
              src={profileImage}
              alt="user"
            />
          ) : initials ? (
            <div className={styles.itemCommentHeader_imgContainer_initials}>
              {initials}
            </div>
          ) : (
            <div className={styles.itemCommentHeader_imgContainer_img}>
              <NoImage width={60} height={60} />
            </div>
          )}
        </div>
        <div className={styles.input}>
          <TextareaAutosize
            placeholder="Agregar comentario"
            className={styles.input_item}
            id="comment"
            style={{ height: 40 }}
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
          />
          <div
            onClick={() =>
              !submittingComment &&
              commentContent.length >= 2 &&
              handlePostComment(id, commentContent)
            }
            className={`${styles.input_item_iconContainer} ${commentContent.length >= 2 && !submittingComment ? styles.input_item_iconContainer_active : ""} ${submittingComment ? styles.input_item_iconContainer_loading : ""}`}
          >
            <Send size={16} />
          </div>
        </div>
      </div>

      {comments.length === 0 && commentsCount > 0 ? (
        <p className={styles.itemCommentBody_loading} aria-live="polite">
          Cargando comentarios…
        </p>
      ) : null}

      {Array.isArray(comments) &&
        comments.length > 0 &&
        comments.map((comment, index) => {
          const commentCompleteName =
            comment.user.type === "COMPANY"
              ? comment.user.razon_social ?? ""
              : `${comment.user.name ?? ""} ${comment.user.last_name ?? ""}`.trim()
          const commentImageProfile = comment.user.image_profile
          const contenido = comment.content
          const creacion = comment.createdAt
          const isMyComment = comment.user.id === userData?.id
          const commentId = Number(comment.id)
          const likeCount = comment?.likes?.length ?? 0
          const isMyLike = comment?.likes?.some(
            (like) => like.user.id === userData?.id
          )
          const commentResponses = comment?.commentResponse
          const intials = commentCompleteName
            .split(" ")
            .map((n) => n[0])
            .join("")
          const profileImageError =
            comment.user.image_profile === DEFAULT_PROFILE_IMAGE
          return (
            <motion.div
              variants={responseVariants}
              initial="hidden"
              animate="visible"
              className={styles.itemCommentBody_item}
              key={index}
            >
              <div className={styles.itemCommentBody_item_imgContainer}>
                {!profileImageError && commentImageProfile ? (
                  <img
                    className={styles.itemCommentBody_item_imgContainer_img}
                    src={commentImageProfile}
                    alt="user"
                  />
                ) : (
                  <div className={styles.itemCommentBody_item_imgContainer_initials}>
                    {intials}
                  </div>
                )}
              </div>
              <div className={styles.itemCommentBody_item_contentContainer}>
                <div
                  className={`${styles.itemCommentBody_item_content} ${isHovered ? styles.itemCommentBody_item_content_hovered : ""}`}
                >
                  <div className={styles.itemCommentBody_item_content_header}>
                    <div className={styles.itemCommentBody_item_content_header_name}>
                      {commentCompleteName}
                      <span className={styles.itemCommentBody_item_content_header_time}>
                        {calcularDiferenciaTiempo(creacion)}
                      </span>
                    </div>
                    {isMyComment && (
                      <button
                        className={styles.itemCommentBody_menuButton}
                        onClick={() => handleToggleMenu(commentId)}
                      >
                        . . .
                        {menu === commentId && (
                          <div className={styles.itemCommentBody_menu}>
                            <div
                              className={styles.itemCommentBody_menu_item}
                              onClick={() => handleMenuAction("Editar", commentId)}
                            >
                              Editar
                            </div>
                            <div
                              className={styles.itemCommentBody_menu_item}
                              onClick={() =>
                                handleMenuAction("Eliminar", commentId)
                              }
                            >
                              Eliminar
                            </div>
                          </div>
                        )}
                      </button>
                    )}
                  </div>
                  {commentEditing === commentId ? (
                    <div className={styles.itemCommentBody_item_content_contentContainerEdit}>
                      <TextareaAutosize
                        className={styles.itemCommentBody_input_item}
                        id="comment"
                        minRows={3}
                        value={editComment || contenido}
                        onChange={(e) => setEditComment(e.target.value)}
                      />
                      <div className={styles.itemCommentBody_input_item_buttonsContainer}>
                        <div
                          className={styles.itemCommentBody_input_item_iconContainer}
                          onClick={() => setCommentEditing(false)}
                        >
                          <X size={16} />
                        </div>
                        <div
                          className={`${styles.itemCommentBody_input_item_iconContainer} ${editComment.length > 0 && loadingAction !== `edit-${commentId}` ? styles.itemCommentBody_input_item_iconContainer_active : ""} ${loadingAction === `edit-${commentId}` ? styles.itemCommentBody_input_item_iconContainer_loading : ""}`}
                          onClick={() =>
                            editComment?.length > 0 &&
                            loadingAction !== `edit-${commentId}` &&
                            handleEditComment(commentId, editComment)
                          }
                        >
                          <Send size={16} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.itemCommentBody_item_text}>
                      {contenido}
                    </div>
                  )}
                </div>
                <div className={styles.itemCommentBody_item_likesAndRespond}>
                  <button
                    className={`${styles.itemCommentBody_item_likesAndRespond_btn} ${isMyLike ? styles.itemCommentBody_item_likesAndRespond_btn_active : ""} ${loadingAction === `like-${commentId}` ? styles.itemCommentBody_item_likesAndRespond_btn_loading : ""}`}
                    onClick={() => loadingAction !== `like-${commentId}` && handleLikeComment(commentId)}
                  >
                    <Heart size={16} />
                    {likeCount > 0 && (
                      <span className={styles.itemCommentBody_item_likesAndRespond_btn_icon}>
                        {likeCount}
                      </span>
                    )}
                    Me gusta
                  </button>
                  <button
                    className={styles.itemCommentBody_item_likesAndRespond_btn}
                    onClick={() => handleShowResponseInput(commentId)}
                  >
                    Responder
                  </button>
                </div>
                {showResponseInput === commentId && (
                  <div className={styles.itemCommentBody_item_responseContainer}>
                    <div
                      className={
                        styles.itemCommentBody_item_responseContainer_imgContainer
                      }
                    >
                      {!imageError && profileImage ? (
                        <img
                          className={
                            styles.itemCommentBody_item_responseContainer_imgContainer_img
                          }
                          src={profileImage}
                          alt="user"
                        />
                      ) : (
                        <div
                          className={
                            styles.itemCommentBody_item_responseContainer_imgContainer_img
                          }
                        >
                          <NoImage width={25} height={25} />
                        </div>
                      )}
                    </div>
                    <div
                      className={
                        styles.itemCommentBody_item_responseContainer_inputContainer
                      }
                    >
                      <TextareaAutosize
                        placeholder="Escribe una respuesta..."
                        className={
                          styles.itemCommentBody_item_responseContainer_input
                        }
                        id="comment"
                        style={{ height: 40 }}
                        value={commentResponse}
                        onChange={(e) => setCommentResponse(e.target.value)}
                      />
                      <div
                        className={`${styles.itemCommentBody_item_responseContainer_iconContainer} ${commentResponse.length >= 2 && loadingAction !== `response-${commentId}` ? styles.itemCommentBody_item_responseContainer_iconContainer_active : ""} ${loadingAction === `response-${commentId}` ? styles.itemCommentBody_item_responseContainer_iconContainer_loading : ""}`}
                        onClick={() =>
                          commentResponse.length >= 2 &&
                          loadingAction !== `response-${commentId}` &&
                          handleResponseComment(commentId, commentResponse)
                        }
                      >
                        <Send size={16} />
                      </div>
                    </div>
                  </div>
                )}
                {commentResponses && commentResponses.length > 0 && (
                  <ResponseComments
                    commentResponses={commentResponses}
                    calcularDiferenciaTiempo={calcularDiferenciaTiempo}
                    isHovered={isHovered}
                  />
                )}
              </div>
            </motion.div>
          )
        })}
    </>
  )
}

const mapStateToProps = () => ({})

export default connect(mapStateToProps, {
  deletePostComment,
  editPostComment,
  createPostComment,
  likePostComment,
  responsePostComment,
})(FeedCardComment)
